export class Point {
    constructor(
        private _x: number = 0,
        private _y: number = 0,
    ) {}

    get x(): number {
        return this._x;
    }

    set x(value: number) {
        this._x = value;
    }

    get y(): number {
        return this._y;
    }

    set y(value: number) {
        this._y = value;
    }

    public distance(other: Point) {
        return Math.sqrt(Math.pow(other.x - this.x, 2) + Math.pow(other.y - this.y, 2));
    }

    normalize(width: number, height: number): Point {
        return new Point(
            this.x / width,
            this.y / height
        );
    }

    denormalize(width: number, height: number): Point {
        return new Point(
            this.x * width,
            this.y * height
        );
    }

    toArray(): number[] {
        return [this.x, this.y];
    }

    static fromArray(data: number[]): Point {
        return new Point(data[0], data[1]);
    }
}

export class Matrix {
    constructor(public elements: number[][]) {}

    get rect(): Rect {
        return new Rect(
            this.elements[2][0],
            this.elements[2][1],
            this.elements[0][0],
            this.elements[1][1],
        );
    }

    public toString = (): string => {
        return `Matrix [${this.elements[0].join(',')}, ${this.elements[1].join(',')}, ${this.elements[2].join(',')}]`;
    };

    public toArray(): number[] {
        return this.elements[0].concat(this.elements[1], this.elements[2]);
    }

    public static fromArray(data: number[]): Matrix {
        return new Matrix([
            [data[0], data[1], data[2]],
            [data[3], data[4], data[5]],
            [data[6], data[7], data[8]],
        ]);
    }

    x(b: Matrix): Matrix {
        let i = this.elements.length;
        const nj = b.elements[0].length;
        const cols = this.elements[0].length;
        const elements = [];
        let sum: number;
        let c: any;
        let j: number = 0;

        while (i--) {
            j = nj;
            elements[i] = [];
            while (j--) {
                c = cols;
                sum = 0;
                while (c--) {
                    sum += this.elements[i][c] * b.elements[c][j];
                }
                elements[i][j] = sum;
            }
        }

        return new Matrix(elements);
    }

    dup(): Matrix {
        return new Matrix([
            [this.elements[0][0], this.elements[0][1], this.elements[0][2]],
            [this.elements[1][0], this.elements[1][1], this.elements[1][2]],
            [this.elements[2][0], this.elements[2][1], this.elements[2][2]],
        ]);
    }

    static Identity(): Matrix {
        return new Matrix([
            [1, 0, 0],
            [0, 1, 0],
            [0, 0, 1],
        ]);
    }
}

export class Size {
    constructor(
        private _width: number,
        private _height: number,
    ) {}

    get width(): number {
        return this._width;
    }

    set width(value: number) {
        this._width = value;
    }

    get height(): number {
        return this._height;
    }

    set height(value: number) {
        this._height = value;
    }
}

export class Rect {
    constructor(
        private _x: number = 0,
        private _y: number = 0,
        private _width: number = 0,
        private _height: number = 0,
    ) {}

    get x(): number {
        return this._x;
    }

    set x(value: number) {
        this._x = value;
    }

    get y(): number {
        return this._y;
    }

    set y(value: number) {
        this._y = value;
    }

    get width(): number {
        return this._width;
    }

    set width(value: number) {
        this._width = value;
    }

    get height(): number {
        return this._height;
    }

    set height(value: number) {
        this._height = value;
    }

    get top(): number {
        return this._y;
    }

    get left(): number {
        return this.x;
    }

    get bottom(): number {
        return this._y + this.height;
    }

    get right(): number {
        return this._x + this._width;
    }

    get ratio(): number {
        return this.width / this.height;
    }

    get position(): Point {
        return new Point(this._x, this._y);
    }

    public toString = (): string => {
        return `Rect [${this.x}, ${this.y}, ${this.width}, ${this.height}]`;
    };

    toArray(): number[] {
        return [this.x, this.y, this.width, this.height];
    }

    zero(): void {
        this._x = this._y = this._width = this._height = 0;
    }

    intersects(other: Rect): boolean {
        if (other.width == 0 || other.height == 0) {
            return false;
        }

        return !(
            other.left > this.right ||
            other.right < this.left ||
            other.top > this.bottom ||
            other.bottom < this.top
        );
    }

    containsPoint(x: number, y: number) {
        return !(x > this.right || x < this.left || y > this.bottom || y < this.top);
    }

    fit(width, height): Size {
        const ratio = width / height;
        let scale;

        if (this.ratio > ratio) {
            scale = width / this.width;
        } else {
            scale = height / this.height;
        }

        return new Size(this.width * scale, this.height * scale);
    }

    clamp(width: number, height: number): void {
        this.x = Math.max(0, this.x);
        this.y = Math.max(0, this.y);
        if (this.x + this.width > width) {
            this.x = width - this.width;
        }
        if (this.y + this.height > height) {
            this.y = height - this.height;
        }
    }

    center(): Point {
        return new Point((this.width / 2), (this.height / 2));
    }

    equals(other: Rect): boolean {
        return (
            this.x === other.x >> 0 &&
            this.y === other.y >> 0 &&
            this.width === other.width >> 0 &&
            this.height === other.height >> 0
        );
    }

    area(): number {
        return this._width * this._height;
    }

    toViewportSpace(point: Point): Point {
        const vx = ((point.x - this.x) / this.width);
        const vy = ((point.y - this.y) / this.height);

        return new Point(vx, vy);
    }

    fromViewportSpace(point: Point): Point {
        const x = (point.x * this.width) + this.x;
        const y = (point.y * this.height) + this.y;

        return new Point(x, y);
    }

    static fromDomRect(domRect: DOMRect) {
        return new Rect(domRect.x, domRect.y, domRect.width, domRect.height);
    }
}
